import { AlertCircle, Loader2 } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * Shared loading / error / empty primitives for data-backed views.
 *
 * Tables already render their own column-shaped states inside `DataTable`;
 * these primitives are for the non-table surfaces (detail pages, dashboards,
 * panels) standardized as the admin data layer migrates to React Query.
 */

export function LoadingState({
  label = "Loading…",
  className,
}: {
  label?: string;
  className?: string;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "flex flex-col items-center justify-center gap-2 py-12 text-gray-400",
        className
      )}
    >
      <Loader2 className="h-6 w-6 animate-spin" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

export function ErrorState({
  message,
  onRetry,
  className,
}: {
  message: string;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col items-center justify-center gap-3 py-12 text-center text-red-600",
        className
      )}
    >
      <AlertCircle className="h-6 w-6" />
      <div className="text-sm">{message}</div>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="rounded-lg border border-red-200 px-3 py-1.5 font-medium text-red-700 text-sm transition-colors hover:bg-red-50"
        >
          Try again
        </button>
      ) : null}
    </div>
  );
}

export function EmptyState({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center py-12 text-center text-gray-400 text-sm",
        className
      )}
    >
      {children}
    </div>
  );
}
